/**
 * DISTRIBUTION-MAP MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

import { useMemo } from "react";
import { buildMapSvg, VIEWBOX } from "../../../../../map-core/render.js";

// Thin React shell over the shared map-core renderer. The SVG content (states,
// labels, Northeast callout boxes) is built by map-core so the editor, export,
// and embed all render identically. Clicks/hover are delegated via data-code.
//
// Three render modes, all through the same renderer:
//  - national (default): states, with `mixedCodes` states drawn as the by-county
//    hatch.
//  - county drill: pass `geometry` (countyGeometry(bundle)) + a zoomed `viewBox`;
//    counties become the clickable/hoverable shapes (data-code = county FIPS).
export default function USMap({
  fills,                  // Map<code, string[]>  (from map-core resolveFills)
  unassignedColor,
  bgColor,
  labelColor = "#1f2937",
  showLabels = true,
  onStateClick,           // (code) => void   (code = state ISO or county FIPS)
  onHover,                // (code | null) => void
  mixedCodes = null,      // Set<ISO> of by-county states -> hatch (national view)
  geometry = null,        // county geometry -> county drill view
  useCallouts = true,     // Northeast callout column (national only)
  viewBox = VIEWBOX,      // zoomed bbox for county drill
}) {
  const inner = useMemo(
    () => buildMapSvg({
      fills, unassignedColor, labelColor, showLabels,
      mixedCodes: mixedCodes || null,
      geometry: geometry || undefined,
      useCallouts,
    }),
    [fills, unassignedColor, labelColor, showLabels, mixedCodes, geometry, useCallouts]
  );

  function codeFrom(e) {
    const el = e.target.closest?.("[data-code]");
    return el ? el.getAttribute("data-code") : null;
  }

  return (
    <svg
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="US distribution map"
      style={{ background: bgColor || "transparent" }}
      onClick={(e) => { const c = codeFrom(e); if (c) onStateClick?.(c); }}
      onMouseOver={(e) => onHover?.(codeFrom(e))}
      onMouseLeave={() => onHover?.(null)}
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
}
